import { Link, useSearchParams } from "react-router-dom";
import { BookOpen } from "lucide-react";

import { useLearnIndex } from "@/lib/api/hooks";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/app-shell";
import type { ArticleCard } from "@/types/api";

/**
 * LearnScreen — mock-matched.
 *
 * Sunken header band: 'LEARN' + '▶ Guides, explainers, and references...'.
 * Two-tab pill toggle: Guides | Articles. The active tab is solid-filled,
 * inactive is a lighter sunken tone. List rows are thumbnail (left
 * square) + UPPERCASE title + body snippet. Tapping a row routes to
 * /app/learn/:slug.
 */
export function LearnScreen() {
  const { data, isLoading } = useLearnIndex();
  // Tab lives in the URL (?tab=articles) so returning from an article
  // ("BACK TO LEARN" links to /app/learn?tab=articles) restores the tab
  // the reader was on instead of resetting to Guides.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: "guide" | "article" =
    searchParams.get("tab") === "articles" ? "article" : "guide";
  const setTab = (t: "guide" | "article") =>
    setSearchParams(t === "article" ? { tab: "articles" } : {}, {
      replace: true,
    });

  if (isLoading) {
    return (
      <div className="h-[20rem] bg-[var(--surface-sunken)] animate-pulse" />
    );
  }
  const items = data ?? [];
  const guides = items.filter((i) => i.kind === "guide");
  const articles = items.filter((i) => i.kind === "article");
  const visible = tab === "guide" ? guides : articles;

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Learn"
        description="Guides, explainers, and references for your stack."
      />

      <div className="flex flex-col gap-25">
        {/* Tab toggle */}
        <div className="grid grid-cols-2 p-2">
          <TabBtn active={tab === "guide"} onClick={() => setTab("guide")}>
            Guides
          </TabBtn>
          <TabBtn active={tab === "article"} onClick={() => setTab("article")}>
            Articles
          </TabBtn>
        </div>

        <div className="flex flex-col gap-25 px-15 md:px-40 max-w-[65rem] mx-auto w-full">
          {/* List */}
          {visible.length === 0 ? (
            <div className="border border-line-subtle px-15 py-30 text-center">
              <BookOpen
                size={20}
                strokeWidth={1.5}
                className="mx-auto text-ink-muted"
              />
              <div className="type-label-md text-ink-muted mt-15">
                No {tab === "guide" ? "guides" : "articles"} yet
              </div>
            </div>
          ) : (
            groupByCategory(visible).map((group) => (
              <section
                key={group.category ?? "__more"}
                className="flex flex-col gap-10"
              >
                {group.showHeading && (
                  <h2 className="type-label-sm text-ink-muted">
                    {(group.category ?? "More").toUpperCase()}
                  </h2>
                )}
                <ul className="flex flex-col gap-10">
                  {group.items.map((item) => (
                    <li key={item.slug}>
                      <LearnRow item={item} />
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Group cards by `category`, preserving backend order (it sorts by
 * learnCategory.order → article order). Uncategorized cards collect
 * into a trailing "More" group. Headings only render when there's more
 * than one group — a single anonymous group keeps the old flat-list
 * look (guides tab, Sanity-down fallback).
 */
function groupByCategory(items: ArticleCard[]): Array<{
  category: string | null;
  items: ArticleCard[];
  showHeading: boolean;
}> {
  const groups: { category: string | null; items: ArticleCard[] }[] = [];
  const byCategory = new Map<string, ArticleCard[]>();
  const uncategorized: ArticleCard[] = [];
  for (const item of items) {
    const cat = item.category ?? null;
    if (cat === null) {
      uncategorized.push(item);
      continue;
    }
    let bucket = byCategory.get(cat);
    if (!bucket) {
      bucket = [];
      byCategory.set(cat, bucket);
      groups.push({ category: cat, items: bucket });
    }
    bucket.push(item);
  }
  if (uncategorized.length) {
    groups.push({ category: null, items: uncategorized });
  }
  const multi = groups.length > 1;
  return groups.map((g) => ({ ...g, showHeading: multi }));
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "h-40 type-body-md transition-colors",
        active
          ? "bg-[var(--accent)] text-white"
          : "bg-[var(--accent-hover)] text-white",
      )}
    >
      {children}
    </button>
  );
}

function LearnRow({ item }: { item: ArticleCard }) {
  return (
    <Link
      to={
        item.kind === 'guide'
          ? `/app/learn/${item.slug}`
          : `/app/learn/article/${item.slug}`
      }
      className="flex items-stretch border border-line-subtle bg-[var(--surface-page)] hover:bg-[var(--surface-raised)]"
    >
      <img
        src="/brand/clinic-scene.png"
        alt=""
        aria-hidden
        className="w-[90px] shrink-0 aspect-square object-cover bg-[var(--surface-sunken)]"
      />
      <div className="flex-1 min-w-0 px-15 py-15 flex flex-col gap-5 justify-center">
        <div className="type-label-md text-ink leading-tight">
          {item.title.toUpperCase()}
        </div>
        {item.snippet && (
          <p className="type-body-sm text-ink-secondary leading-snug">
            {item.snippet}
          </p>
        )}
      </div>
    </Link>
  );
}
